/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSelectedCity, useUserData } from "../Utility";
import Header from "../enduser/components/Header";
import Footer from "../enduser/components/Footer";
import SocialProfilesSection from "../enduser/components/SocialProfilesSection";
import ContactUsBanner from "../enduser/components/ContactUsBanner";
import WelcomeEnquiryModal from "../enduser/components/WelcomeEnquiryModal";
import GoldenStrip from "../enduser/components/GoldenStrip";
import "./MainLayout.css";
import { isBusinessUser } from "../utils/UtilityFunctions";

const WELCOME_ENQUIRY_DISMISSED_KEY = "welcomeEnquiryDismissed";

function MainLayout({
  children,
  showHeader = true,
  showFooter = true,
  basicHeaderFooter = false,
}) {
  const isBusiness = isBusinessUser();
  const [isWelcomeEnquiryOpen, setIsWelcomeEnquiryOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isLoggedIn = useSelector((state) => state.utilityReducer?.isLoggedIn);


  const loginData = useUserData();

  const storedCity = useSelectedCity();
  const pathname = location.pathname;

  // Incomplete business users may only access the business profile completion page
  const isIncompleteBusinessUser =
    loginData !== null && loginData !== undefined
      ? isLoggedIn && isBusiness && loginData?.status !== "approved"
      : true;

  const isHomePath =
    pathname === "/" || pathname.startsWith("/city/");

  // Show welcome enquiry modal 5 seconds after user lands on home (only when not logged in, once per browser)
  useEffect(() => {
    if (isLoggedIn) return;
    if (!isHomePath) return;
    if (localStorage.getItem(WELCOME_ENQUIRY_DISMISSED_KEY)) return;
    const timer = setTimeout(() => {
      setIsWelcomeEnquiryOpen(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isLoggedIn, isHomePath]);

  // If city is in Redux but missing from URL, add it to URL (e.g. user returns with stored city).
  // On homepage with no city in URL, do NOT inject stored city so both dropdowns show "Select city".
  useEffect(() => {
    if (!storedCity) return;
    const urlCity = searchParams.get("city");
    if (urlCity) return; // URL has city - never overwrite
    if (pathname === "/" || pathname.startsWith("/city/")) return; // Home uses / or /city/…, not ?city=
    if (pathname.startsWith("/venues") || pathname.startsWith("/vendors"))
      return; // City is encoded in the path for listings
    const next = new URLSearchParams(searchParams);
    next.set("city", storedCity);
    setSearchParams(next, { replace: true });
  }, [storedCity, searchParams, setSearchParams, pathname]);

  // ?openLogin=user or business -> redirect to login page
  useEffect(() => {
    if (!isHomePath) return;
    const openLogin = searchParams.get("openLogin");
    if (openLogin === "user" && !isLoggedIn) {
      setSearchParams({});
      navigate("/login");
    }
    if (openLogin === "business" && !isLoggedIn) {
      setSearchParams({});
      navigate("/login?type=business");
    }
  }, [isHomePath, searchParams, isLoggedIn, setSearchParams, navigate]);

  const openModal = () => {
    if (isLoggedIn) {
      navigate("/profile");
      return;
    }
    navigate("/login");
  };

  const openBusinessLoginModal = () => {
    if (isLoggedIn) {
      navigate("/profile");
      return;
    }
    navigate("/register?type=business");
  };

  const openEnquiryForm = () => {
    navigate("/contact");
  };

  const closeWelcomeEnquiry = () => {
    localStorage.setItem(WELCOME_ENQUIRY_DISMISSED_KEY, "true");
    setIsWelcomeEnquiryOpen(false);
  };

  const handleWelcomeSendEnquiry = (quickQuery) => {
    closeWelcomeEnquiry();
    navigate("/enquiry", { state: quickQuery ? { quickQuery } : {} });
  };

  useEffect(() => {
    if (!(isLoggedIn && loginData !== null && loginData !== undefined && isBusiness)) {
      return;
    }

    // Only force redirect for incomplete business profiles.
    if (isIncompleteBusinessUser && pathname !== "/profile/business") {
      navigate("/profile/business", { replace: true });
    }
  }, [isLoggedIn, loginData, isBusiness, isIncompleteBusinessUser, pathname, navigate]);
  return (
    <div className="main-layout">
      <>
        {showHeader && (
          <Header
            basic={basicHeaderFooter}
            onProfileClick={openModal}
            onEnquireClick={openEnquiryForm}
            onListBusinessClick={openBusinessLoginModal}
          />
        )}
        <main className="main-content">
          <GoldenStrip />
          {children}
        </main>
        {showFooter && (
          <>
            {!basicHeaderFooter && <SocialProfilesSection />}

            {!basicHeaderFooter && isHomePath && !isLoggedIn && (
              <ContactUsBanner onEnquireClick={openEnquiryForm} />
            )}
            <Footer basic={basicHeaderFooter} />
          </>
        )}
        {!isLoggedIn && (
          <WelcomeEnquiryModal
            isOpen={isWelcomeEnquiryOpen}
            onClose={closeWelcomeEnquiry}
            onSendEnquiry={handleWelcomeSendEnquiry}
          />
        )}
      </>
    </div>
  );
}

export default MainLayout;
